/**
 * Public Directory page - displays all public quizzes
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PublicDirectory.css';
import { QuestionType } from '../db/schema';

interface Quiz {
    id: number;
    title: string;
    description: string;
    userId: number;
    isPublic: number;
    ownerUsername: string;
    questionTypes?: number[];
    questionCount?: number;
}

const questionTypeLabels: Record<QuestionType, string> = {
    [QuestionType.SHORT_ANSWER]: 'Short Answer',
    [QuestionType.MULTIPLE_CHOICE]: 'Multiple Choice',
    [QuestionType.TRUE_FALSE]: 'True/False',
};

export default function PublicDirectory() {
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [filteredQuizzes, setFilteredQuizzes] = useState<Quiz[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [creatingGroupQuiz, setCreatingGroupQuiz] = useState<number | null>(
        null
    );

    const fetchPublicQuizzes = async () => {
        try {
            const response = await fetch('/api/quizzes/public');
            if (response.ok) {
                const data = await response.json();
                setQuizzes(data.quizzes);
                setFilteredQuizzes(data.quizzes);
            }
        } catch (error) {
            console.error('Failed to fetch public quizzes:', error);
        }
    };

    useEffect(() => {
        fetchPublicQuizzes();
    }, []);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        filterQuizzes(searchQuery);
    };

    const filterQuizzes = (query: string) => {
        if (!query.trim()) {
            setFilteredQuizzes(quizzes);
            return;
        }

        const lowerQuery = query.toLowerCase();
        const filtered = quizzes.filter(
            (quiz) =>
                quiz.title.toLowerCase().includes(lowerQuery) ||
                quiz.description.toLowerCase().includes(lowerQuery)
        );
        setFilteredQuizzes(filtered);
    };

    const handleStartGroupQuiz = async (quizId: number) => {
        setCreatingGroupQuiz(quizId);
        try {
            const response = await fetch('/api/group-quiz/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ quizId }),
            });

            if (!response.ok) {
                throw new Error('Failed to create group quiz');
            }

            const data = await response.json();
            navigate(`/group-quiz/${data.hash}/host`);
        } catch (error) {
            console.error('Failed to create group quiz:', error);
            alert('Failed to create group quiz. Please try again.');
        } finally {
            setCreatingGroupQuiz(null);
        }
    };

    return (
        <div className="publicDirectory">
            <h1 className="publicDirectoryTitle">Public Directory</h1>
            <p className="publicDirectorySubtitle">
                Browse and play quizzes shared by the community
            </p>

            <form className="searchForm" onSubmit={handleSearch}>
                <input
                    type="text"
                    className="searchInput"
                    placeholder="Search quizzes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="searchButton">
                    Search
                </button>
            </form>

            {quizzes.length === 0 ? (
                <div className="noQuizzes">
                    <p>No public quizzes available yet.</p>
                </div>
            ) : filteredQuizzes.length === 0 ? (
                <div className="noQuizzes">
                    <p>No quizzes found matching your search.</p>
                </div>
            ) : (
                filteredQuizzes.map((quiz) => (
                    <div key={quiz.id} className="publicQuizItem">
                        <h3 className="publicQuizTitle">{quiz.title}</h3>
                        <span className="quizSeparator">•</span>
                        {quiz.description && (
                            <>
                                <span className="publicQuizDescription">
                                    {quiz.description}
                                </span>
                            </>
                        )}
                        <span className="quizOwner">
                            by {quiz.ownerUsername}
                        </span>
                        {quiz.questionCount !== undefined && (
                            <>
                                <span className="quizSeparator">•</span>
                                <span className="questionCount">
                                    {quiz.questionCount} question
                                    {quiz.questionCount !== 1 ? 's' : ''}
                                </span>
                            </>
                        )}
                        {quiz.questionTypes &&
                            quiz.questionTypes.length > 0 && (
                                <>
                                    <span className="quizSeparator">•</span>
                                    <div className="questionTypeTags">
                                        {quiz.questionTypes.map((type) => (
                                            <span
                                                key={type}
                                                className="questionTypeTag"
                                            >
                                                {
                                                    questionTypeLabels[
                                                        type as QuestionType
                                                    ]
                                                }
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                        <div className="publicQuizActions">
                            <button
                                className="playButton"
                                onClick={() =>
                                    navigate(`/quiz/${quiz.id}/take`)
                                }
                                title="Take Quiz"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="16"
                                    height="16"
                                >
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                Play
                            </button>
                            <button
                                className="groupPlayButton"
                                onClick={() => handleStartGroupQuiz(quiz.id)}
                                disabled={creatingGroupQuiz === quiz.id}
                                title="Start Group Quiz"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    width="16"
                                    height="16"
                                    strokeWidth="2"
                                >
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {creatingGroupQuiz === quiz.id
                                    ? 'Creating...'
                                    : 'Group Play'}
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}
